import { normalizeRealtimeEngineConfig, REALTIME_ALERT_LEVELS } from '../features/realtime/config';
import RealtimeAlertRubric from './realtime/RealtimeAlertRubric';
import { getRealtimeErrorMessage } from '../features/realtime/errors';
import type { CreatePromptRequest, EngineKind, RealtimeEngineConfig } from '../services/api';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Save, Copy, Edit2, AlertCircle, CheckCircle, Wand2, ChevronDown, SlidersHorizontal, ListTree, Filter } from 'lucide-react';
import {
  Prompt,
  PromptExecutionConfig,
  PromptSelectionFilter,
  PromptSemanticIndexingConfig,
  VideoLevelPromptConfig,
  apiService,
  getApiErrorMessage,
} from '../services/api';
import PromptSchemaFieldsEditor, { generateExampleObject } from './prompt-schema/PromptSchemaFieldsEditor';
import PromptSemanticIndexingFieldPicker from './PromptSemanticIndexingFieldPicker';
import {
  buildPromptCreationDraftFromAssistantResult,
  createDefaultPromptCreationDraft,
  formatPromptDraftSchemaJson,
  normalizePromptCreationDraft,
  PromptCreationDraft,
  SchemaEditorMode,
} from '../features/prompt-creation/draft';
import {
  collectPromptSemanticIndexingLeafFieldOptions,
  PromptSchemaAdditionalProperties,
  PromptSchemaFieldDefinition,
  getDuplicatePromptSchemaFieldNames,
  getBuilderSchemaAllowsAdditionalProperties,
  getPromptSemanticIndexingValidationError,
  getPromptSchemaFieldValidationError,
  hasPromptSemanticIndexingEmbeddableCapacity,
  isAdditionalPropertiesEnabled,
  mergePromptSchemaWithOriginal,
  promptSchemaFieldsToJsonSchema,
  sanitizePromptSemanticIndexingDisabledFields,
  schemaToBuilderPromptSchemaFields,
  schemaToPromptSchemaFields,
  validatePromptJsonSchema,
} from './prompt-schema/schemaFields';
import { useTabActivity } from '../contexts/TabActivityContext';
import './PromptDetailSidebar.css';
import './AssistantPanel.css';
import PromptExecutionConfigEditor from './PromptExecutionConfigEditor';
import WorkflowFilterEditor from './WorkflowFilterEditor';
import { extractConditionFieldsFromSchemas } from '../utils/conditionSearchFields';
import type { EngineEditorTab } from '../features/workflows/presentation';
import {
  DEFAULT_PROMPT_EXECUTION_CONFIG,
  hasPromptSelectionFilter,
  normalizePromptExecutionConfig,
  normalizePromptSelectionFilter,
  validateAndBuildSelectionFilter,
} from '../features/workflows/config';

interface PromptDetailSidebarProps {
  engineKind?: EngineKind;
  onPersist?: (data: CreatePromptRequest) => Promise<Prompt>;
  prompt: Prompt | null;
  isOpen: boolean;
  isVisible?: boolean;
  onClose: () => void;
  onSave?: (prompt: Prompt) => void;
  isCreating?: boolean;
  createDraft?: PromptCreationDraft | null;
  onCreateDraftChange?: (draft: PromptCreationDraft) => void;
  onDiscardCreateDraft?: () => void;
  readOnly?: boolean;
  initialTab?: EngineEditorTab;
  onTabChange?: (tab: EngineEditorTab) => void;
  onUnsavedChangesChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}

const INTEGER_DEFAULT_DRAFT_PATTERN = /^[+-]?\d+$/;
const NUMBER_DEFAULT_DRAFT_PATTERN = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;
const INCOMPLETE_NUMBER_DEFAULT_PATTERN = /(?:\.$|[eE][+-]?)$/;
const NON_NEGATIVE_INTEGER_DRAFT_PATTERN = /^(?:0|[1-9]\d*)$/;

const SIDEBAR_NEW_FIELD_DEFAULTS: Partial<PromptSchemaFieldDefinition> = {
  required: true,
};

const ENGINE_EDITOR_STAGES = {
  processing: { number: 2, title: 'Processing', description: 'Prepare clips for extraction', icon: SlidersHorizontal },
  extraction: { number: 1, title: 'Extraction fields', description: 'Describe each clip and the whole video', icon: ListTree },
  filters: { number: 3, title: 'Filters', description: 'Select the results you want to keep', icon: Filter },
};

const serializeCreateDraft = (draft: PromptCreationDraft | null | undefined): string => {
  if (!draft) {
    return '';
  }
  try {
    return JSON.stringify(draft);
  } catch {
    return '';
  }
};

const getBuilderToRawModeSwitchError = (
  fields: PromptSchemaFieldDefinition[],
  fieldLabel: string,
): string | null => {
  const getLossyNonNegativeIntegerConstraintLabel = (
    value: unknown,
    label: string,
  ): string | null => {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue || NON_NEGATIVE_INTEGER_DRAFT_PATTERN.test(trimmedValue)) {
      return null;
    }

    return label;
  };

  const getLossyNumberConstraintLabel = (
    value: unknown,
    label: string,
  ): string | null => {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    if (
      !trimmedValue
      || (
        NUMBER_DEFAULT_DRAFT_PATTERN.test(trimmedValue)
        && !INCOMPLETE_NUMBER_DEFAULT_PATTERN.test(trimmedValue)
      )
    ) {
      return null;
    }

    return label;
  };

  if (fields.some((field) => !field.name.trim())) {
    return `Finish naming all ${fieldLabel}s before switching to raw mode`;
  }

  const duplicateFieldNames = getDuplicatePromptSchemaFieldNames(fields);
  if (duplicateFieldNames.length > 0) {
    return `Resolve duplicate ${fieldLabel} names before switching to raw mode`;
  }

  for (const field of fields) {
    if (field.type === 'integer' && typeof field.defaultValue === 'string') {
      const trimmedDefaultValue = field.defaultValue.trim();
      if (trimmedDefaultValue && !INTEGER_DEFAULT_DRAFT_PATTERN.test(trimmedDefaultValue)) {
        return `Finish the numeric default for field "${field.name}" before switching to raw mode`;
      }
    }

    if (field.type === 'number' && typeof field.defaultValue === 'string') {
      const trimmedDefaultValue = field.defaultValue.trim();
      if (
        trimmedDefaultValue
        && (
          !NUMBER_DEFAULT_DRAFT_PATTERN.test(trimmedDefaultValue)
          || INCOMPLETE_NUMBER_DEFAULT_PATTERN.test(trimmedDefaultValue)
        )
      ) {
        return `Finish the numeric default for field "${field.name}" before switching to raw mode`;
      }
    }

    const lossyAdvancedConstraintLabel = (() => {
      if (!field.advancedOptions) {
        return null;
      }

      const constraintLabels: string[] = [];
      if (
        field.type === 'string'
        || field.type === 'enum'
        || field.type === 'array_string'
        || field.type === 'array_enum'
      ) {
        constraintLabels.push(
          getLossyNonNegativeIntegerConstraintLabel(
            field.advancedOptions.minLength,
            field.type.startsWith('array_') ? 'item min length' : 'min length',
          ) || '',
          getLossyNonNegativeIntegerConstraintLabel(
            field.advancedOptions.maxLength,
            field.type.startsWith('array_') ? 'item max length' : 'max length',
          ) || '',
        );
      }

      if (
        field.type === 'integer'
        || field.type === 'number'
        || field.type === 'array_integer'
        || field.type === 'array_number'
      ) {
        constraintLabels.push(
          getLossyNumberConstraintLabel(
            field.advancedOptions.minimum,
            field.type.startsWith('array_') ? 'item minimum' : 'minimum',
          ) || '',
          getLossyNumberConstraintLabel(
            field.advancedOptions.maximum,
            field.type.startsWith('array_') ? 'item maximum' : 'maximum',
          ) || '',
        );
      }

      if (field.type.startsWith('array_')) {
        constraintLabels.push(
          getLossyNonNegativeIntegerConstraintLabel(field.advancedOptions.minItems, 'min items') || '',
          getLossyNonNegativeIntegerConstraintLabel(field.advancedOptions.maxItems, 'max items') || '',
        );
      }

      if (field.type === 'object' || field.type === 'array_object') {
        constraintLabels.push(
          getLossyNonNegativeIntegerConstraintLabel(
            field.advancedOptions.minProperties,
            field.type === 'array_object' ? 'item min properties' : 'min properties',
          ) || '',
          getLossyNonNegativeIntegerConstraintLabel(
            field.advancedOptions.maxProperties,
            field.type === 'array_object' ? 'item max properties' : 'max properties',
          ) || '',
        );
      }

      return constraintLabels.find(Boolean) || null;
    })();

    if (lossyAdvancedConstraintLabel) {
      return `Finish the ${lossyAdvancedConstraintLabel} constraint for field "${field.name}" before switching to raw mode`;
    }

    if (field.nestedFields && field.nestedFields.length > 0) {
      const nestedError = getBuilderToRawModeSwitchError(field.nestedFields, 'nested field');
      if (nestedError) {
        return nestedError;
      }
    }
  }

  return null;
};

const getInvalidBuilderToRawModeSwitchError = (
  fields: PromptSchemaFieldDefinition[],
  fieldLabel: string,
  allowAdditionalProperties: PromptSchemaAdditionalProperties,
): string | null => {
  const validationError = getPromptSchemaFieldValidationError(fields, fieldLabel, {
    allowAdditionalProperties,
  });
  if (!validationError) {
    return null;
  }

  const hasNamedFields = fields.some((field) => field.name.trim());
  if (!hasNamedFields && allowAdditionalProperties === false) {
    return null;
  }

  return validationError;
};

const PromptDetailSidebar: React.FC<PromptDetailSidebarProps> = ({
  prompt,
  isOpen,
  isVisible = true,
  onClose,
  onSave,
  isCreating = false,
  createDraft = null,
  onCreateDraftChange,
  onDiscardCreateDraft,
  readOnly = false,
  initialTab = 'extraction',
  engineKind = 'video',
  onPersist,
  onTabChange,
  onUnsavedChangesChange,
  onSavingChange,
}) => {
  const isRealtime = engineKind === 'realtime' || prompt?.is_realtime === true;
  const hasProcessingStage = !isRealtime || isCreating;
  const initialSection = initialTab === 'processing' && !hasProcessingStage ? 'extraction' : initialTab;
  const [expandedStages, setExpandedStages] = useState<Record<EngineEditorTab, boolean>>({
    processing: initialSection === 'processing',
    extraction: initialSection === 'extraction',
    filters: initialSection === 'filters',
  });
  const stageRefs = useRef<Partial<Record<EngineEditorTab, HTMLDivElement | null>>>({});
  const sidebarContentRef = useRef<HTMLDivElement>(null);
  const promptNameRef = useRef<HTMLInputElement>(null);
  const [expandedSections, setExpandedSections] = useState({ segment: true, video: true, preview: false });
  const changeTab = useCallback((tab: EngineEditorTab) => {
    setExpandedStages((previous) => ({ ...previous, [tab]: true }));
    onTabChange?.(tab);
    requestAnimationFrame(() => stageRefs.current[tab]?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }));
  }, [onTabChange]);
  useEffect(() => {
    if (!isOpen) return;
    setExpandedStages((previous) => ({ ...previous, [initialSection]: true }));
    const frame = requestAnimationFrame(() => {
      if (initialSection === 'extraction' && sidebarContentRef.current) sidebarContentRef.current.scrollTop = 0;
      else stageRefs.current[initialSection]?.scrollIntoView?.({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [initialSection, isOpen, prompt?.prompt_id]);
  useEffect(() => {
    setExpandedSections({ segment: true, video: true, preview: false });
    setMessage(null);
  }, [prompt?.prompt_id, isCreating]);
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('promptSidebarWidth');
    return saved ? parseInt(saved, 10) : 900;
  });
  const { isActive } = useTabActivity();
  const [isResizing, setIsResizing] = useState(false);
  const [isEditing, setIsEditing] = useState(isCreating);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  const [realtimeConfig, setRealtimeConfig] = useState<RealtimeEngineConfig>(() => normalizeRealtimeEngineConfig());

  // Form fields
  const [promptName, setPromptName] = useState('');
  const [promptDescription, setPromptDescription] = useState('');
  const [promptText, setPromptText] = useState('');
  const [fields, setFields] = useState<PromptSchemaFieldDefinition[]>([]);
  const [schemaAllowsAdditionalProperties, setSchemaAllowsAdditionalProperties] = useState<PromptSchemaAdditionalProperties>(false);
  const [videoLevelEnabled, setVideoLevelEnabled] = useState(false);
  const [videoLevelInstructions, setVideoLevelInstructions] = useState('');
  const [videoLevelFields, setVideoLevelFields] = useState<PromptSchemaFieldDefinition[]>([]);
  const [videoLevelSchemaAllowsAdditionalProperties, setVideoLevelSchemaAllowsAdditionalProperties] = useState<PromptSchemaAdditionalProperties>(false);
  const [segmentSchemaMode, setSegmentSchemaMode] = useState<SchemaEditorMode>('simple');
  const [segmentRawSchemaText, setSegmentRawSchemaText] = useState('');
  const [videoLevelSchemaMode, setVideoLevelSchemaMode] = useState<SchemaEditorMode>('simple');
  const [videoLevelRawSchemaText, setVideoLevelRawSchemaText] = useState('');
  const [semanticIndexingDisabledSegmentFields, setSemanticIndexingDisabledSegmentFields] = useState<string[]>([]);
  const [semanticIndexingDisabledVideoLevelFields, setSemanticIndexingDisabledVideoLevelFields] = useState<string[]>([]);
  const [executionConfig, setExecutionConfig] = useState<PromptExecutionConfig>({ ...DEFAULT_PROMPT_EXECUTION_CONFIG });
  const executionConfigRef = useRef(executionConfig);
  executionConfigRef.current = executionConfig;
  const [selectionFilter, setSelectionFilter] = useState<PromptSelectionFilter | null>(null);
  const [assistantPanelOpen, setAssistantPanelOpen] = useState(false);
  const [assistantPrompt, setAssistantPrompt] = useState('');
  const [assistantIsLoading, setAssistantIsLoading] = useState(false);
  const [assistantError, setAssistantError] = useState<string | null>(null);
  const isSourceReadOnly = readOnly || (!isCreating && prompt?.user_id === 'system');

  const modalRef = useRef<HTMLDivElement>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);
  const originalSegmentSchemaRef = useRef<Record<string, any> | null>(null);
  const originalVideoLevelSchemaRef = useRef<Record<string, any> | null>(null);
  const lastValidSegmentRawSchemaRef = useRef<Record<string, any> | null>(null);
  const assistantAbortRef = useRef<AbortController | null>(null);
  const initialCreateDraftRef = useRef<PromptCreationDraft | null>(normalizePromptCreationDraft(createDraft));
  const lastPublishedCreateDraftRef = useRef<string>(
    serializeCreateDraft(normalizePromptCreationDraft(createDraft)),
  );
  const lastHydratedCreateDraftRef = useRef<string>(
    serializeCreateDraft(normalizePromptCreationDraft(createDraft)),
  );
  const suppressCreateDraftPublishRef = useRef(false);
  const savedFormSignatureRef = useRef('');

  const buildSegmentSchemaFromBuilder = (): Record<string, any> => mergePromptSchemaWithOriginal(
    promptSchemaFieldsToJsonSchema(fields, {
      allowAdditionalProperties: schemaAllowsAdditionalProperties,
    }),
    originalSegmentSchemaRef.current,
  );

  const buildVideoLevelSchemaFromBuilder = (): Record<string, any> => mergePromptSchemaWithOriginal(
    promptSchemaFieldsToJsonSchema(videoLevelFields, {
      allowAdditionalProperties: videoLevelSchemaAllowsAdditionalProperties,
    }),
    originalVideoLevelSchemaRef.current,
  );

  const parseRawSchema = (
    schemaText: string,
    label: string,
    options: { allowEmptyObjects?: boolean } = {},
  ): { schema?: Record<string, any>; error?: string } => {
    try {
      const parsed = JSON.parse(schemaText);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { error: `${label} must be a JSON object` };
      }

      const schemaValidationError = validatePromptJsonSchema(parsed as Record<string, any>, options);
      if (schemaValidationError) {
        return { error: schemaValidationError };
      }

      return { schema: parsed as Record<string, any> };
    } catch (error) {
      return { error: `${label} must be valid JSON` };
    }
  };

  const parseSegmentRawSchema = (): { schema?: Record<string, any>; error?: string } => (
    parseRawSchema(segmentRawSchemaText, 'Raw segment schema')
  );

  const parseVideoLevelRawSchema = (): { schema?: Record<string, any>; error?: string } => (
    parseRawSchema(videoLevelRawSchemaText, 'Raw video-level schema')
  );

  const switchSegmentSchemaMode = (nextMode: SchemaEditorMode): boolean => {
    if (nextMode === segmentSchemaMode) {
      return true;
    }

    if (segmentSchemaMode === 'raw' && nextMode !== 'raw') {
      const rawSchemaResult = parseRawSchema(segmentRawSchemaText, 'Raw segment schema', {
        allowEmptyObjects: true,
      });
      if (rawSchemaResult.error || !rawSchemaResult.schema) {
        setMessage({ type: 'error', text: rawSchemaResult.error || 'Failed to parse raw segment schema' });
        return false;
      }

      originalSegmentSchemaRef.current = rawSchemaResult.schema;
      lastValidSegmentRawSchemaRef.current = rawSchemaResult.schema;
      setFields(schemaToBuilderPromptSchemaFields(rawSchemaResult.schema));
      setSchemaAllowsAdditionalProperties(
        getBuilderSchemaAllowsAdditionalProperties(rawSchemaResult.schema),
      );
      setSegmentRawSchemaText(formatPromptDraftSchemaJson(rawSchemaResult.schema));
    } else if (segmentSchemaMode !== 'raw' && nextMode === 'raw') {
      const builderToRawError = getBuilderToRawModeSwitchError(fields, 'field');
      if (builderToRawError) {
        setMessage({ type: 'error', text: builderToRawError });
        return false;
      }

      const invalidBuilderError = getInvalidBuilderToRawModeSwitchError(
        fields,
        'field',
        schemaAllowsAdditionalProperties,
      );
      if (invalidBuilderError) {
        setMessage({ type: 'error', text: invalidBuilderError });
        return false;
      }

      const nextRawSchema = buildSegmentSchemaFromBuilder();
      lastValidSegmentRawSchemaRef.current = nextRawSchema;
      setSegmentRawSchemaText(formatPromptDraftSchemaJson(nextRawSchema));
    }

    setSegmentSchemaMode(nextMode);
    return true;
  };

  const switchVideoLevelSchemaMode = (nextMode: SchemaEditorMode): boolean => {
    if (nextMode === videoLevelSchemaMode) {
      return true;
    }

    if (videoLevelSchemaMode === 'raw' && nextMode !== 'raw') {
      const rawSchemaResult = parseRawSchema(videoLevelRawSchemaText, 'Raw video-level schema', {
        allowEmptyObjects: true,
      });
      if (rawSchemaResult.error || !rawSchemaResult.schema) {
        setMessage({ type: 'error', text: rawSchemaResult.error || 'Failed to parse raw video-level schema' });
        return false;
      }

      originalVideoLevelSchemaRef.current = rawSchemaResult.schema;
      setVideoLevelFields(
        schemaToBuilderPromptSchemaFields(rawSchemaResult.schema),
      );
      setVideoLevelSchemaAllowsAdditionalProperties(
        getBuilderSchemaAllowsAdditionalProperties(rawSchemaResult.schema),
      );
      setVideoLevelRawSchemaText(formatPromptDraftSchemaJson(rawSchemaResult.schema));
    } else if (videoLevelSchemaMode !== 'raw' && nextMode === 'raw') {
      const builderToRawError = getBuilderToRawModeSwitchError(
        videoLevelFields,
        'video-level output field',
      );
      if (builderToRawError) {
        setMessage({ type: 'error', text: builderToRawError });
        return false;
      }

      const invalidBuilderError = getInvalidBuilderToRawModeSwitchError(
        videoLevelFields,
        'video-level output field',
        videoLevelSchemaAllowsAdditionalProperties,
      );
      if (invalidBuilderError) {
        setMessage({ type: 'error', text: invalidBuilderError });
        return false;
      }

      setVideoLevelRawSchemaText(formatPromptDraftSchemaJson(buildVideoLevelSchemaFromBuilder()));
    }

    setVideoLevelSchemaMode(nextMode);
    return true;
  };

  const applyPromptFormState = useCallback((nextPrompt: Prompt) => {
    originalSegmentSchemaRef.current = nextPrompt.json_schema || null;
    originalVideoLevelSchemaRef.current = nextPrompt.video_level?.json_schema || null;
    lastValidSegmentRawSchemaRef.current = nextPrompt.json_schema || { type: 'object', properties: {} };
    setRealtimeConfig(normalizeRealtimeEngineConfig(nextPrompt.realtime_config));
    setPromptName(nextPrompt.name);
    setPromptDescription(nextPrompt.description);
    setPromptText(nextPrompt.prompt_text);
    setFields(schemaToBuilderPromptSchemaFields(nextPrompt.json_schema));
    setSchemaAllowsAdditionalProperties(
      getBuilderSchemaAllowsAdditionalProperties(nextPrompt.json_schema),
    );
    setVideoLevelEnabled(Boolean(nextPrompt.video_level));
    setVideoLevelInstructions(nextPrompt.video_level?.instructions_text || '');
    setVideoLevelFields(
      nextPrompt.video_level
        ? schemaToBuilderPromptSchemaFields(nextPrompt.video_level.json_schema)
        : [],
    );
    setVideoLevelSchemaAllowsAdditionalProperties(
      nextPrompt.video_level
        ? getBuilderSchemaAllowsAdditionalProperties(nextPrompt.video_level.json_schema)
        : false,
    );
    setSemanticIndexingDisabledSegmentFields(
      nextPrompt.semantic_indexing?.disabled_segment_fields || [],
    );
    setSemanticIndexingDisabledVideoLevelFields(
      nextPrompt.semantic_indexing?.disabled_video_level_fields || [],
    );
    setExecutionConfig(normalizePromptExecutionConfig(nextPrompt.execution_config));
    setSelectionFilter(normalizePromptSelectionFilter(nextPrompt.selection_filter));
    setSegmentSchemaMode('simple');
    setSegmentRawSchemaText(formatPromptDraftSchemaJson(nextPrompt.json_schema));
    setVideoLevelSchemaMode('simple');
    setVideoLevelRawSchemaText(formatPromptDraftSchemaJson(nextPrompt.video_level?.json_schema));
  }, []);

  const applyCreateDraftState = useCallback((draft: PromptCreationDraft) => {
    const parsePersistedSchema = (schemaText: string): Record<string, any> | null => {
      try {
        const parsed = JSON.parse(schemaText);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          return null;
        }
        return parsed as Record<string, any>;
      } catch {
        return null;
      }
    };

    const segmentSchema = parsePersistedSchema(draft.segmentRawSchemaText);
    const videoLevelSchema = parsePersistedSchema(draft.videoLevelRawSchemaText);

    originalSegmentSchemaRef.current = segmentSchema;
    originalVideoLevelSchemaRef.current = videoLevelSchema;
    lastValidSegmentRawSchemaRef.current = segmentSchema || promptSchemaFieldsToJsonSchema(draft.fields, {
      allowAdditionalProperties: draft.schemaAllowsAdditionalProperties,
    });

    setRealtimeConfig(normalizeRealtimeEngineConfig(draft.realtimeConfig));
    setPromptName(draft.name);
    setPromptDescription(draft.description);
    setPromptText(draft.promptText);
    setFields(draft.fields);
    setSchemaAllowsAdditionalProperties(draft.schemaAllowsAdditionalProperties);
    setVideoLevelEnabled(draft.videoLevelEnabled);
    setVideoLevelInstructions(draft.videoLevelInstructions);
    setVideoLevelFields(draft.videoLevelFields);
    setVideoLevelSchemaAllowsAdditionalProperties(draft.videoLevelSchemaAllowsAdditionalProperties);
    setSegmentSchemaMode(draft.segmentSchemaMode);
    setSegmentRawSchemaText(draft.segmentRawSchemaText);
    setVideoLevelSchemaMode(draft.videoLevelSchemaMode);
    setVideoLevelRawSchemaText(draft.videoLevelRawSchemaText);
    setSemanticIndexingDisabledSegmentFields(draft.semanticIndexingDisabledSegmentFields);
    setSemanticIndexingDisabledVideoLevelFields(draft.semanticIndexingDisabledVideoLevelFields);
    setExecutionConfig(normalizePromptExecutionConfig(draft.executionConfig));
    setSelectionFilter(normalizePromptSelectionFilter(draft.selectionFilter));
    setAssistantPrompt(draft.assistantPrompt);
    setAssistantPanelOpen(draft.assistantPanelOpen);
    setAssistantError(null);
  }, []);

  const hydrateCreateDraftState = useCallback((draft: PromptCreationDraft) => {
    const draftSignature = serializeCreateDraft(draft);
    suppressCreateDraftPublishRef.current = true;
    lastPublishedCreateDraftRef.current = draftSignature;
    lastHydratedCreateDraftRef.current = draftSignature;
    applyCreateDraftState(draft);
  }, [applyCreateDraftState]);

  const buildCreateDraftFromCurrentState = useCallback((): PromptCreationDraft => ({
    isRealtime,
    realtimeConfig: isRealtime ? realtimeConfig : null,
    name: promptName,
    description: promptDescription,
    promptText,
    fields,
    schemaAllowsAdditionalProperties,
    videoLevelEnabled,
    videoLevelInstructions,
    videoLevelFields,
    videoLevelSchemaAllowsAdditionalProperties,
    segmentSchemaMode,
    segmentRawSchemaText,
    videoLevelSchemaMode,
    videoLevelRawSchemaText,
    semanticIndexingDisabledSegmentFields,
    semanticIndexingDisabledVideoLevelFields,
    executionConfig,
    selectionFilter,
    assistantPrompt,
    assistantPanelOpen,
  }), [
    isRealtime,
    realtimeConfig,
    assistantPanelOpen,
    assistantPrompt,
    fields,
    promptDescription,
    promptName,
    promptText,
    schemaAllowsAdditionalProperties,
    segmentRawSchemaText,
    segmentSchemaMode,
    semanticIndexingDisabledSegmentFields,
    semanticIndexingDisabledVideoLevelFields,
    executionConfig,
    selectionFilter,
    videoLevelEnabled,
    videoLevelFields,
    videoLevelInstructions,
    videoLevelRawSchemaText,
    videoLevelSchemaAllowsAdditionalProperties,
    videoLevelSchemaMode,
  ]);

  useEffect(() => {
    initialCreateDraftRef.current = normalizePromptCreationDraft(createDraft);
  }, [createDraft]);

  useEffect(() => {
    if (!onUnsavedChangesChange) return;
    const signature = serializeCreateDraft(buildCreateDraftFromCurrentState());
    if (!isEditing) savedFormSignatureRef.current = signature;
    onUnsavedChangesChange(!isCreating && isEditing && signature !== savedFormSignatureRef.current);
  }, [buildCreateDraftFromCurrentState, isCreating, isEditing, onUnsavedChangesChange]);

  useEffect(() => {
    onSavingChange?.(isSaving);
  }, [isSaving, onSavingChange]);

  useEffect(() => {
    const normalizedCreateDraft = normalizePromptCreationDraft(createDraft);
    if (!isCreating || !normalizedCreateDraft) {
      return;
    }

    const incomingDraftSignature = serializeCreateDraft(normalizedCreateDraft);
    if (!incomingDraftSignature || incomingDraftSignature === lastPublishedCreateDraftRef.current) {
      return;
    }

    const currentDraftSignature = serializeCreateDraft(buildCreateDraftFromCurrentState());
    if (
      lastHydratedCreateDraftRef.current
      && currentDraftSignature !== lastHydratedCreateDraftRef.current
    ) {
      return;
    }

    hydrateCreateDraftState(normalizedCreateDraft);
  }, [buildCreateDraftFromCurrentState, createDraft, hydrateCreateDraftState, isCreating]);

  // Initialize form from prompt
  useEffect(() => {
    if (prompt && !isCreating) {
      applyPromptFormState(prompt);
      setIsDuplicating(false);
      setIsEditing(false);
    } else if (isCreating) {
      const initialDraft = initialCreateDraftRef.current || createDefaultPromptCreationDraft();
      hydrateCreateDraftState(initialDraft);
      setIsDuplicating(false);
      setIsEditing(true);
      setMessage(null);
    }
  }, [prompt, isCreating, hydrateCreateDraftState, applyPromptFormState]);

  useEffect(() => {
    if (!isCreating || !onCreateDraftChange) {
      return;
    }
    if (suppressCreateDraftPublishRef.current) {
      suppressCreateDraftPublishRef.current = false;
      return;
    }
    const draft = buildCreateDraftFromCurrentState();
    const draftSignature = serializeCreateDraft(draft);
    if (!draftSignature || draftSignature === lastPublishedCreateDraftRef.current) {
      return;
    }
    lastPublishedCreateDraftRef.current = draftSignature;
    onCreateDraftChange(draft);
  }, [buildCreateDraftFromCurrentState, isCreating, onCreateDraftChange]);

  useEffect(() => {
    setAssistantIsLoading(false);
    return () => {
      assistantAbortRef.current?.abort();
      assistantAbortRef.current = null;
    };
  }, [prompt?.prompt_id, isCreating]);

  // Handle ESC key to close sidebar
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isEditing) {
        onClose();
      }
    };

    if (isOpen && isVisible && isActive) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isActive, isOpen, isVisible, isEditing, onClose]);

  // Save width to localStorage and dispatch event
  useEffect(() => {
    localStorage.setItem('promptSidebarWidth', sidebarWidth.toString());
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('promptSidebarWidthChange', { detail: { width: sidebarWidth } }));
    }
  }, [sidebarWidth, isOpen]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  };

  useEffect(() => {
    if (!isActive || !isOpen || !isVisible) {
      setIsResizing(false);
      return;
    }
    if (!isResizing) return;
    document.body.classList.add('resizing-prompt-sidebar');

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX;
      const newWidth = Math.max(400, Math.min(1400, resizeStartWidth.current + deltaX));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('resizing-prompt-sidebar');
    };
  }, [isActive, isOpen, isResizing, isVisible]);

  const clearDuplicateSchemaMessage = () => {
    const normalizedMessage = message?.text.toLowerCase() || '';
    if (
      message?.type === 'error'
      && (normalizedMessage.includes('duplicate') || normalizedMessage.includes('already exists'))
    ) {
      setMessage(null);
    }
  };

  const segmentRawSchemaResult = segmentSchemaMode === 'raw'
    ? parseSegmentRawSchema()
    : undefined;
  const effectiveSegmentSchema = segmentSchemaMode === 'raw'
    ? (segmentRawSchemaResult?.schema || lastValidSegmentRawSchemaRef.current)
    : undefined;
  const effectiveSegmentFields = effectiveSegmentSchema
    ? schemaToBuilderPromptSchemaFields(effectiveSegmentSchema)
    : fields;
  const effectiveSegmentSchemaAllowsAdditionalProperties = effectiveSegmentSchema
    ? getBuilderSchemaAllowsAdditionalProperties(effectiveSegmentSchema)
    : schemaAllowsAdditionalProperties;
  const videoLevelRawSchemaResult = videoLevelSchemaMode === 'raw'
    ? parseVideoLevelRawSchema()
    : undefined;
  const effectiveVideoLevelSemanticSchema = videoLevelSchemaMode === 'raw'
    ? videoLevelRawSchemaResult?.schema
    : undefined;
  const effectiveVideoLevelSemanticFields = effectiveVideoLevelSemanticSchema
    ? schemaToBuilderPromptSchemaFields(effectiveVideoLevelSemanticSchema)
    : videoLevelFields;
  const effectiveVideoLevelSemanticAdditionalProperties = effectiveVideoLevelSemanticSchema
    ? getBuilderSchemaAllowsAdditionalProperties(effectiveVideoLevelSemanticSchema)
    : videoLevelSchemaAllowsAdditionalProperties;
  const segmentSemanticFieldOptions = collectPromptSemanticIndexingLeafFieldOptions(
    effectiveSegmentFields,
  );
  const videoLevelSemanticFieldOptions = collectPromptSemanticIndexingLeafFieldOptions(
    effectiveVideoLevelSemanticFields,
  );
  const segmentSelectionFields = extractConditionFieldsFromSchemas([
    segmentSchemaMode === 'raw'
      ? effectiveSegmentSchema
      : buildSegmentSchemaFromBuilder(),
  ]);
  const videoSelectionFields = videoLevelEnabled
    ? extractConditionFieldsFromSchemas([
        videoLevelSchemaMode === 'raw'
          ? effectiveVideoLevelSemanticSchema
          : buildVideoLevelSchemaFromBuilder(),
      ])
    : [];

  // Validate fields
  const validateFields = (): { text: string; tab: EngineEditorTab | 'identity' } | null => {
    const invalid = (text: string, tab: EngineEditorTab | 'identity' = 'extraction') => ({ text, tab });
    if (isRealtime && REALTIME_ALERT_LEVELS.some(level => !realtimeConfig.alert_rubric[level].trim())) return invalid('Describe every alert level.', 'filters');
    if (!promptName.trim()) return invalid('Extraction engine name is required', 'identity');
    if (promptName.trim().length < 3) return invalid('Extraction engine name must be at least 3 characters', 'identity');
    if (!promptText.trim()) return invalid('Extraction engine text is required');
    if (promptText.trim().length < 10) return invalid('Extraction engine text must be at least 10 characters');

    if (
      executionConfig.video_segmentation_type === 'fixed'
      && (typeof executionConfig.video_segment_duration !== 'number'
        || !Number.isInteger(executionConfig.video_segment_duration)
        || executionConfig.video_segment_duration < 1
        || executionConfig.video_segment_duration > 300)
    ) {
      return invalid('Video segment duration must be a whole number from 1 to 300 seconds', 'processing');
    }
    if (segmentSchemaMode === 'raw') {
      if (segmentRawSchemaResult?.error) {
        return invalid(segmentRawSchemaResult.error);
      }
    } else {
      const segmentFieldError = getPromptSchemaFieldValidationError(fields, 'field', {
        allowAdditionalProperties: schemaAllowsAdditionalProperties,
      });
      if (segmentFieldError) {
        return invalid(segmentFieldError);
      }
    }

    if (videoLevelEnabled) {
      if (!videoLevelInstructions.trim()) return invalid('Video-level instructions are required');
      if (executionConfig.video_segmentation_type === 'fixed' || executionConfig.audio_segmentation_type === 'fixed') {
        return invalid('Video-level fields require content-aware segmentation');
      }
      if (videoLevelSchemaMode === 'raw') {
        if (videoLevelRawSchemaResult?.error) {
          return invalid(videoLevelRawSchemaResult.error);
        }
      } else {
        const videoLevelFieldError = getPromptSchemaFieldValidationError(
          videoLevelFields,
          'video-level output field',
          {
            allowAdditionalProperties: videoLevelSchemaAllowsAdditionalProperties,
          },
        );
        if (videoLevelFieldError) {
          return invalid(videoLevelFieldError);
        }
      }
    }

    const segmentSemanticError = getPromptSemanticIndexingValidationError(
      semanticIndexingDisabledSegmentFields,
      effectiveSegmentFields,
      {
        allowAdditionalProperties: effectiveSegmentSchemaAllowsAdditionalProperties,
        surfaceLabel: 'segment output',
      },
    );
    const segmentHasEmbeddableCapacity = hasPromptSemanticIndexingEmbeddableCapacity(
      semanticIndexingDisabledSegmentFields,
      effectiveSegmentFields,
      { allowAdditionalProperties: effectiveSegmentSchemaAllowsAdditionalProperties },
    );
    const videoLevelHasEmbeddableCapacity = videoLevelEnabled
      ? hasPromptSemanticIndexingEmbeddableCapacity(
        semanticIndexingDisabledVideoLevelFields,
        effectiveVideoLevelSemanticFields,
        { allowAdditionalProperties: effectiveVideoLevelSemanticAdditionalProperties },
      )
      : false;
    if (!segmentHasEmbeddableCapacity && !videoLevelHasEmbeddableCapacity) {
      const error = videoLevelEnabled
        ? 'Select at least one searchable leaf field for the segment output or video-level output'
        : segmentSemanticError;
      return error ? invalid(error) : null;
    }

    const selectionValidation = validateAndBuildSelectionFilter(
      selectionFilter,
      segmentSelectionFields,
      videoSelectionFields,
    );
    if (selectionValidation.error) {
      return invalid(selectionValidation.error, 'filters');
    }

    return null;
  };

  const isAbortedRequestError = (error: any): boolean => (
    error?.name === 'AbortError' || error?.code === 'ERR_CANCELED'
  );

  const generatePromptAssistantDraft = useCallback(async () => {
    if (!isCreating) {
      return false;
    }

    const normalizedInstruction = assistantPrompt.trim();
    if (!normalizedInstruction) {
      setAssistantError('Describe the extraction engine you want to generate.');
      return false;
    }

    assistantAbortRef.current?.abort();
    const controller = new AbortController();
    assistantAbortRef.current = controller;
    setAssistantIsLoading(true);
    setAssistantError(null);
    setMessage(null);

    const generationExecutionConfig = { ...executionConfigRef.current };
    try {
      const generatedDraft = await apiService.generatePromptDraft(
        normalizedInstruction,
        { executionConfig: generationExecutionConfig },
        controller.signal,
      );
      if (controller.signal.aborted) {
        return false;
      }

      applyCreateDraftState(
        buildPromptCreationDraftFromAssistantResult(generatedDraft, {
          isRealtime,
          realtimeConfig,
          executionConfig: generationExecutionConfig,
        }, normalizedInstruction),
      );
      changeTab('extraction');
      const generatedHasFilters = hasPromptSelectionFilter(generatedDraft.selection_filter);
      setExpandedStages((previous) => ({ ...previous, filters: generatedHasFilters }));
      setMessage({
        type: 'success',
        text: generatedHasFilters
          ? 'Draft generated with optional filters. Review the populated conditions below; all extracted results remain available.'
          : 'Draft generated without filters. All extracted results will be available.',
      });
      setExpandedSections({ segment: true, video: false, preview: false });
      return true;
    } catch (error: any) {
      if (isAbortedRequestError(error)) {
        return false;
      }
      setAssistantError((isRealtime ? getRealtimeErrorMessage : getApiErrorMessage)(error, 'Extraction engine assistant failed. Please try again.'));
      return false;
    } finally {
      if (assistantAbortRef.current === controller) {
        assistantAbortRef.current = null;
        setAssistantIsLoading(false);
      }
    }
  }, [applyCreateDraftState, assistantPrompt, changeTab, isCreating, isRealtime, realtimeConfig]);


  useEffect(() => {
    if (segmentSchemaMode === 'raw' && segmentRawSchemaResult?.schema) {
      lastValidSegmentRawSchemaRef.current = segmentRawSchemaResult.schema;
    }
  }, [segmentSchemaMode, segmentRawSchemaResult?.schema]);

  useEffect(() => {
    const sanitizedDisabledFields = sanitizePromptSemanticIndexingDisabledFields(
      semanticIndexingDisabledSegmentFields,
      effectiveSegmentFields,
    );
    if (
      sanitizedDisabledFields.length !== semanticIndexingDisabledSegmentFields.length
      || sanitizedDisabledFields.some(
        (fieldName, index) => fieldName !== semanticIndexingDisabledSegmentFields[index],
      )
    ) {
      setSemanticIndexingDisabledSegmentFields(sanitizedDisabledFields);
    }
  }, [effectiveSegmentFields, semanticIndexingDisabledSegmentFields]);

  useEffect(() => {
    const sanitizedDisabledFields = sanitizePromptSemanticIndexingDisabledFields(
      semanticIndexingDisabledVideoLevelFields,
      effectiveVideoLevelSemanticFields,
    );
    if (
      sanitizedDisabledFields.length !== semanticIndexingDisabledVideoLevelFields.length
      || sanitizedDisabledFields.some(
        (fieldName, index) => fieldName !== semanticIndexingDisabledVideoLevelFields[index],
      )
    ) {
      setSemanticIndexingDisabledVideoLevelFields(sanitizedDisabledFields);
    }
  }, [effectiveVideoLevelSemanticFields, semanticIndexingDisabledVideoLevelFields]);

  const restorePromptView = useCallback(() => {
    setIsDuplicating(false);
    setIsEditing(false);
    setMessage(null);
    if (prompt) {
      applyPromptFormState(prompt);
    }
  }, [applyPromptFormState, prompt]);

  // Save prompt
  const handleSave = async () => {
    if (isSourceReadOnly && !isCreating && !isDuplicating) {
      setMessage({ type: 'error', text: 'Built-in extraction engines cannot be edited.' });
      return;
    }

    const validationError = validateFields();
    if (validationError) {
      setMessage({ type: 'error', text: validationError.text });
      if (validationError.tab === 'identity') {
        promptNameRef.current?.focus();
        if (sidebarContentRef.current) sidebarContentRef.current.scrollTop = 0;
      } else {
        changeTab(validationError.tab);
      }
      if (validationError.tab === 'extraction') {
        setExpandedSections((previous) => ({ ...previous, segment: true, video: videoLevelEnabled }));
      }
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const jsonSchema = segmentSchemaMode === 'raw'
        ? parseSegmentRawSchema().schema!
        : buildSegmentSchemaFromBuilder();
      let videoLevelPayload: VideoLevelPromptConfig | null = null;
      if (videoLevelEnabled) {
        videoLevelPayload = {
          instructions_text: videoLevelInstructions.trim(),
          json_schema: videoLevelSchemaMode === 'raw'
            ? parseVideoLevelRawSchema().schema!
            : buildVideoLevelSchemaFromBuilder(),
        };
      }
      const semanticIndexingPayload: PromptSemanticIndexingConfig = {
        disabled_segment_fields: sanitizePromptSemanticIndexingDisabledFields(
          semanticIndexingDisabledSegmentFields,
          effectiveSegmentFields,
        ),
        disabled_video_level_fields: videoLevelEnabled
          ? sanitizePromptSemanticIndexingDisabledFields(
            semanticIndexingDisabledVideoLevelFields,
            effectiveVideoLevelSemanticFields,
          )
          : [],
      };
      const selectionFilterPayload = validateAndBuildSelectionFilter(
        selectionFilter,
        extractConditionFieldsFromSchemas([jsonSchema]),
        videoLevelPayload ? extractConditionFieldsFromSchemas([videoLevelPayload.json_schema]) : [],
      ).filter;
      const executionConfigPayload = normalizePromptExecutionConfig(executionConfig);

      const realtimePayload = isRealtime ? { is_realtime: true, realtime_config: { alert_rubric: Object.fromEntries(REALTIME_ALERT_LEVELS.map(level => [level, realtimeConfig.alert_rubric[level].trim()])) } as RealtimeEngineConfig } : {};
      let savedPrompt: Prompt;
      if (onPersist) {
        savedPrompt = await onPersist({ name: promptName.trim(), description: promptDescription.trim(), prompt_text: promptText.trim(), json_schema: jsonSchema, semantic_indexing: semanticIndexingPayload, selection_filter: selectionFilterPayload, ...realtimePayload });
      } else if (isCreating || isDuplicating || !prompt) {
        // Create new prompt
        savedPrompt = await apiService.createPrompt({
          ...realtimePayload,
          name: promptName.trim(),
          description: promptDescription.trim() || '',
          prompt_text: promptText.trim(),
          json_schema: jsonSchema,
          video_level: videoLevelPayload,
          semantic_indexing: semanticIndexingPayload,
          execution_config: executionConfigPayload,
          selection_filter: selectionFilterPayload,
        });
        setMessage({
          type: 'success',
          text: isDuplicating ? 'Extraction engine duplicated successfully!' : 'Extraction engine created successfully!',
        });
        if (isCreating) {
          suppressCreateDraftPublishRef.current = true;
          onDiscardCreateDraft?.();
        }
      } else {
        // Update existing prompt
        savedPrompt = await apiService.updatePrompt(prompt.prompt_id, {
          ...realtimePayload,
          name: promptName.trim(),
          description: promptDescription.trim(),
          prompt_text: promptText.trim(),
          json_schema: jsonSchema,
          video_level: videoLevelPayload,
          semantic_indexing: semanticIndexingPayload,
          execution_config: executionConfigPayload,
          selection_filter: selectionFilterPayload,
          clear_video_level: !videoLevelEnabled && Boolean(prompt.video_level),
        });
        setMessage({ type: 'success', text: 'Extraction engine updated successfully!' });
      }

      applyPromptFormState(savedPrompt);
      setIsDuplicating(false);
      setIsEditing(false);
      if (onSave) {
        onSave(savedPrompt);
      }

    } catch (error: any) {
      console.error('Failed to save prompt:', error?.response?.data || error);
      const errorMessage = (isRealtime ? getRealtimeErrorMessage : getApiErrorMessage)(error, 'Failed to save extraction engine. Please try again.');
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSaving(false);
    }
  };

  // Duplicate prompt
  const handleDuplicate = () => {
    const baseName = promptName.trim() || prompt?.name || 'Untitled Extraction Engine';
    setPromptName(baseName.endsWith(' (Copy)') ? baseName : `${baseName} (Copy)`);
    setMessage(null);
    setIsDuplicating(true);
    setIsEditing(true);
  };

  const enterEditMode = () => {
    if (isSourceReadOnly) {
      return;
    }
    setMessage(null);
    setIsDuplicating(false);
    setIsEditing(true);
  };

  const panelTitle = isCreating
    ? 'Create New Extraction Engine'
    : isDuplicating
      ? 'Duplicate Extraction Engine'
      : isEditing
        ? 'Edit Extraction Engine'
        : 'Extraction Engine Details';
  const saveButtonLabel = isCreating
    ? 'Create Extraction Engine'
    : isDuplicating
      ? 'Duplicate Extraction Engine'
      : 'Save Changes';

  if (!isOpen) return null;

  const segmentRawPreviewSchema = segmentSchemaMode === 'raw'
    ? parseSegmentRawSchema().schema
    : undefined;
  const videoLevelRawPreviewSchema = videoLevelSchemaMode === 'raw'
    ? parseVideoLevelRawSchema().schema
    : undefined;
  const segmentPreviewFields = segmentSchemaMode === 'raw'
    ? segmentRawPreviewSchema
      ? schemaToPromptSchemaFields(segmentRawPreviewSchema)
      : fields
    : fields;
  const videoLevelPreviewFields = videoLevelSchemaMode === 'raw'
    ? videoLevelRawPreviewSchema
      ? schemaToPromptSchemaFields(videoLevelRawPreviewSchema)
      : videoLevelFields
    : videoLevelFields;

  const renderStageHeader = (stage: EngineEditorTab) => {
    const stageInfo = ENGINE_EDITOR_STAGES[stage];
    const { icon: Icon } = stageInfo;
    const number = stage === 'filters' && !hasProcessingStage ? 2 : stageInfo.number;
    const title = isRealtime ? ({ processing: 'Assistant', extraction: 'Extraction fields', filters: 'Alerts & filters' })[stage] : stageInfo.title;
    const description = isRealtime ? ({ processing: 'Generate or refine extraction fields', extraction: 'Define the fields for every capture window', filters: 'Describe each alert level and optionally filter results' })[stage] : stageInfo.description;
    return (
      <button
        type="button"
        id={'engine-editor-stage-' + stage}
        className="engine-editor-stage-toggle"
        aria-expanded={expandedStages[stage]}
        aria-controls={'engine-editor-panel-' + stage}
        onClick={() => {
          if (expandedStages[stage]) {
            setExpandedStages((previous) => ({ ...previous, [stage]: false }));
          } else {
            changeTab(stage);
          }
        }}
      >
        <span className="engine-editor-step">{number}</span>
        <Icon size={17} aria-hidden="true" />
        <span className="engine-editor-stage-label">
          <strong>{title}</strong>
          <span>{description}</span>
        </span>
        <ChevronDown size={17} className="engine-editor-stage-chevron" aria-hidden="true" />
      </button>
    );
  };

  return (
    <>
      {isVisible && isActive && <div className="prompt-detail-backdrop" aria-hidden="true" onClick={onClose} />}
      <div
        className={`prompt-detail-sidebar ${isOpen ? 'sidebar-open' : ''} ${isResizing ? 'resizing' : ''}`}
        ref={modalRef}
        onKeyDown={event => { if (event.key === 'Escape') event.preventDefault(); }}
        style={{ width: `${sidebarWidth}px`, display: isVisible ? undefined : 'none' }}
      >
        {/* Resize handle */}
        <div
          className={`resize-handle ${isResizing ? 'resizing' : ''}`}
          onMouseDown={handleResizeStart}
        />

        <div className="sidebar-header">
          <div className="engine-editor-identity">
            <span>{panelTitle}</span>
            {isEditing ? (
              <input
                className="engine-editor-title-input"
                type="text"
                ref={promptNameRef}
                value={promptName}
                onChange={(event) => setPromptName(event.target.value)}
                placeholder="Enter extraction engine name..."
                aria-label="Extraction engine title"
                aria-required="true"
                maxLength={100}
              />
            ) : (
              <h2>{promptName.trim() || prompt?.name || 'New extraction engine'}</h2>
            )}
          </div>
          <div className="header-actions">
            {!isCreating && !isEditing && (
              <>
                {!onPersist && <button
                  className="action-button engine-editor-header-action"
                  onClick={handleDuplicate}
                  title="Duplicate Extraction Engine"
                >
                  <Copy size={16} /><span>Customize a copy</span>
                </button>}
                {!isSourceReadOnly && (
                  <button
                    className="action-button engine-editor-header-action"
                    onClick={enterEditMode}
                    title="Edit Extraction Engine"
                  >
                    <Edit2 size={16} /><span>Edit</span>
                  </button>
                )}
              </>
            )}
            <button className="close-button" onClick={onClose} aria-label="Close engine editor">
              <X size={20} />
            </button>
          </div>
        </div>

        {message && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {message.text}
          </div>
        )}

        <div className="sidebar-content" ref={sidebarContentRef}>
          <div className="engine-editor-layout">
            <div className="engine-editor-content">
              <div className="prompt-info-section engine-editor-identity-fields">
                  <div className="form-group compact">
                    <label>Description</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={promptDescription}
                        onChange={(e) => setPromptDescription(e.target.value)}
                        placeholder="Optional..."
                        maxLength={200}
                      />
                    ) : (
                      <div className="readonly-value">{promptDescription || '—'}</div>
                    )}
                  </div>
              </div>
              <div className="engine-editor-stage" ref={(node) => { stageRefs.current.extraction = node; }}>
                {renderStageHeader('extraction')}
              <div className="engine-editor-stage-body" id="engine-editor-panel-extraction" role="region" aria-labelledby="engine-editor-stage-extraction" hidden={!expandedStages.extraction}>
              {/* Segment-Level Section */}
              <div className="schema-section engine-schema-section">
                <button
                  type="button"
                  className="engine-schema-section-toggle"
                  aria-expanded={expandedSections.segment}
                  aria-controls="engine-segment-fields"
                  onClick={() => setExpandedSections((previous) => ({ ...previous, segment: !previous.segment }))}
                >
                  <ChevronDown size={16} className={expandedSections.segment ? 'expanded' : ''} aria-hidden="true" />
                  <span>{isRealtime ? 'Observation fields' : 'Segment fields'}</span>
                  <span className="prompt-execution-value-chip">{segmentPreviewFields.length} fields</span>
                  <span className="prompt-execution-value-chip">{isRealtime ? 'Per capture' : 'Per segment'}</span>
                </button>
                <div className="engine-schema-section-body" id="engine-segment-fields" hidden={!expandedSections.segment}>
                <p className="section-helper">
                  {isRealtime ? 'Extract these fields from every capture window. Use + add to define and review a new field.' : 'Extract these fields from each video segment or individual image.'}
                </p>

                <div className="form-group">
                  <div className="schema-editor-header">
                    <label>Output Fields</label>
                    {isEditing && (
                      <div className="schema-mode-actions" role="tablist" aria-label="Segment schema mode">
                        <button
                          className={`schema-mode-button ${segmentSchemaMode === 'simple' ? 'active' : ''}`}
                          onClick={() => switchSegmentSchemaMode('simple')}
                          type="button"
                          role="tab"
                          aria-selected={segmentSchemaMode === 'simple'}
                        >
                          Simple
                        </button>
                        <button
                          className={`schema-mode-button ${segmentSchemaMode === 'advanced' ? 'active' : ''}`}
                          onClick={() => switchSegmentSchemaMode('advanced')}
                          type="button"
                          role="tab"
                          aria-selected={segmentSchemaMode === 'advanced'}
                        >
                          Advanced
                        </button>
                        <button
                          className={`schema-mode-button ${segmentSchemaMode === 'raw' ? 'active' : ''}`}
                          onClick={() => switchSegmentSchemaMode('raw')}
                          type="button"
                          role="tab"
                          aria-selected={segmentSchemaMode === 'raw'}
                        >
                          Raw
                        </button>
                      </div>
                    )}
                  </div>
                  {segmentSchemaMode === 'raw' ? (
                    <div className="schema-advanced-editor">
                      <textarea
                        readOnly={!isEditing}
                        value={segmentRawSchemaText}
                        onChange={(event) => setSegmentRawSchemaText(event.target.value)}
                        rows={14}
                        spellCheck={false}
                        aria-label="Raw segment JSON schema"
                      />
                      <div className="schema-mode-hint">
                        Use raw mode when you need to paste or edit the full schema JSON directly.
                      </div>
                    </div>
                  ) : (
                    <PromptSchemaFieldsEditor
                      fields={fields}
                      addFieldPresentation={isRealtime ? 'dialog' : 'inline'}
                      onFieldsChange={setFields}
                      editable={isEditing}
                      emptyStateText={isEditing ? 'Click "+ add" to define fields' : 'No fields defined'}
                      schemaAllowsAdditionalProperties={schemaAllowsAdditionalProperties}
                      onSchemaAllowsAdditionalPropertiesChange={setSchemaAllowsAdditionalProperties}
                      onFieldNameConflict={(fieldName) => {
                        setMessage({ type: 'error', text: `Duplicate field name: ${fieldName}` });
                      }}
                      onFieldNameConflictResolved={clearDuplicateSchemaMessage}
                      showPreview={false}
                      mode={segmentSchemaMode === 'advanced' ? 'advanced' : 'simple'}
                      newFieldDefaults={SIDEBAR_NEW_FIELD_DEFAULTS}
                    />
                  )}
                </div>

                <div className="form-group">
                  {isEditing && <label>Semantic Search Fields</label>}
                  <PromptSemanticIndexingFieldPicker
                    fieldOptions={segmentSemanticFieldOptions}
                    disabledFieldPaths={isEditing
                      ? semanticIndexingDisabledSegmentFields
                      : (prompt?.semantic_indexing?.disabled_segment_fields || [])}
                    onChange={setSemanticIndexingDisabledSegmentFields}
                    allowDynamicFields={isAdditionalPropertiesEnabled(
                      effectiveSegmentSchemaAllowsAdditionalProperties,
                    )}
                    readonly={!isEditing}
                    disabled={!isEditing}
                    emptyStateText={null}
                  />
                </div>
                <div className="form-group engine-extraction-instructions">
                  <label>Instructions *</label>
                  {isEditing ? (
                    <textarea
                      value={promptText}
                      onChange={(e) => setPromptText(e.target.value)}
                      placeholder="Enter the extraction engine text that will be sent to the AI..."
                      rows={12}
                    />
                  ) : (
                    <div className="readonly-value prompt-text" tabIndex={0}>{promptText}</div>
                  )}
                </div>

                </div>
              </div>

              {/* Video-Level Section */}
              {!isRealtime && <div className="schema-section engine-schema-section">
                <div className="engine-video-section-heading">
                  <button
                    type="button"
                    className="engine-schema-section-toggle"
                    aria-expanded={expandedSections.video}
                    aria-controls="engine-video-fields"
                    onClick={() => setExpandedSections((previous) => ({ ...previous, video: !previous.video }))}
                  >
                    <ChevronDown size={16} className={expandedSections.video ? 'expanded' : ''} aria-hidden="true" />
                    <span>Video fields</span>
                    <span className="prompt-execution-value-chip">{videoLevelEnabled ? videoLevelPreviewFields.length + ' fields' : 'Off'}</span>
                    {videoLevelEnabled && <span className="prompt-execution-value-chip">Whole video</span>}
                  </button>
                  {isEditing && (
                    <label className="engine-video-enable">
                      <input
                        type="checkbox"
                        checked={videoLevelEnabled}
                        onChange={(event) => {
                          setVideoLevelEnabled(event.target.checked);
                          if (event.target.checked) setExpandedSections((previous) => ({ ...previous, video: true }));
                        }}
                        aria-label="Enable video-level extraction"
                      />
                      <span>{videoLevelEnabled ? 'Enabled' : 'Enable'}</span>
                    </label>
                  )}
                </div>
                <div className="engine-schema-section-body" id="engine-video-fields" hidden={!expandedSections.video}>
                {!videoLevelEnabled ? (
                  <p className="engine-editor-empty">This engine extracts segment fields only. {isEditing ? 'Enable video fields to add a grounded whole-video response.' : 'Customize this engine to add a whole-video summary.'}</p>
                ) : (
                  <p className="section-helper">
                  Extract these fields from the video’s visual and audio content.
                  </p>
                )}

                <div className="engine-video-fields-content" hidden={!videoLevelEnabled}>
                    <div className="form-group">
                      <div className="schema-editor-header">
                        <label>Output Fields</label>
                        {isEditing && videoLevelEnabled && (
                          <div className="schema-mode-actions" role="tablist" aria-label="Video-level schema mode">
                            <button
                              className={`schema-mode-button ${videoLevelSchemaMode === 'simple' ? 'active' : ''}`}
                              onClick={() => switchVideoLevelSchemaMode('simple')}
                              type="button"
                              role="tab"
                              aria-selected={videoLevelSchemaMode === 'simple'}
                            >
                              Simple
                            </button>
                            <button
                              className={`schema-mode-button ${videoLevelSchemaMode === 'advanced' ? 'active' : ''}`}
                              onClick={() => switchVideoLevelSchemaMode('advanced')}
                              type="button"
                              role="tab"
                              aria-selected={videoLevelSchemaMode === 'advanced'}
                            >
                              Advanced
                            </button>
                            <button
                              className={`schema-mode-button ${videoLevelSchemaMode === 'raw' ? 'active' : ''}`}
                              onClick={() => switchVideoLevelSchemaMode('raw')}
                              type="button"
                              role="tab"
                              aria-selected={videoLevelSchemaMode === 'raw'}
                            >
                              Raw
                            </button>
                          </div>
                        )}
                      </div>
                      {videoLevelSchemaMode === 'raw' ? (
                        <div className="schema-advanced-editor">
                          <textarea
                            value={videoLevelRawSchemaText}
                            onChange={(event) => setVideoLevelRawSchemaText(event.target.value)}
                            rows={14}
                            spellCheck={false}
                            aria-label="Raw video-level JSON schema"
                            readOnly={!isEditing}
                            disabled={!videoLevelEnabled}
                          />
                          <div className="schema-mode-hint">
                            Use raw mode when you need to paste or edit the full schema JSON directly.
                          </div>
                        </div>
                      ) : (
                        <PromptSchemaFieldsEditor
                          fields={videoLevelFields}
                          onFieldsChange={setVideoLevelFields}
                          editable={isEditing && videoLevelEnabled}
                          schemaAllowsAdditionalProperties={videoLevelSchemaAllowsAdditionalProperties}
                          onSchemaAllowsAdditionalPropertiesChange={setVideoLevelSchemaAllowsAdditionalProperties}
                          emptyStateText={isEditing ? 'Click "+ add" to define fields' : 'No fields defined'}
                          onFieldNameConflict={(fieldName) => {
                            setMessage({ type: 'error', text: `Duplicate video-level field name: ${fieldName}` });
                          }}
                          onFieldNameConflictResolved={clearDuplicateSchemaMessage}
                          showPreview={false}
                          mode={videoLevelSchemaMode === 'advanced' ? 'advanced' : 'simple'}
                          newFieldDefaults={SIDEBAR_NEW_FIELD_DEFAULTS}
                        />
                      )}
                    </div>

                    <div className="form-group">
                      {isEditing && <label>Semantic Search Fields</label>}
                      <PromptSemanticIndexingFieldPicker
                        fieldOptions={videoLevelSemanticFieldOptions}
                        disabledFieldPaths={isEditing
                          ? semanticIndexingDisabledVideoLevelFields
                          : (prompt?.semantic_indexing?.disabled_video_level_fields || [])}
                        onChange={setSemanticIndexingDisabledVideoLevelFields}
                        allowDynamicFields={isAdditionalPropertiesEnabled(
                          effectiveVideoLevelSemanticAdditionalProperties,
                        )}
                        readonly={!isEditing}
                        disabled={!videoLevelEnabled || !isEditing}
                        emptyStateText={null}
                      />
                    </div>
                  <div className="form-group engine-extraction-instructions">
                      <label>Instructions {isEditing && videoLevelEnabled && '*'}</label>
                      <div className="instructions-editor">
                        {isEditing ? (
                          <textarea
                            value={videoLevelInstructions}
                            onChange={(e) => setVideoLevelInstructions(e.target.value)}
                            placeholder="Describe the whole-video fields to extract from visible and audible evidence..."
                            rows={12}
                            className="instructions-textarea"
                            disabled={!videoLevelEnabled}
                          />
                        ) : (
                          <div className="readonly-value instructions-readonly" tabIndex={0}>{videoLevelInstructions}</div>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
              }
              <div className="schema-section engine-schema-section">
                <button
                  type="button"
                  className="engine-schema-section-toggle"
                  aria-expanded={expandedSections.preview}
                  aria-controls="engine-response-preview"
                  onClick={() => setExpandedSections((previous) => ({ ...previous, preview: !previous.preview }))}
                >
                  <ChevronDown size={16} className={expandedSections.preview ? 'expanded' : ''} aria-hidden="true" />
                  <span>Example output</span>
                  <span className="prompt-execution-value-chip">Preview</span>
                </button>
                <div id="engine-response-preview" className="engine-schema-section-body" hidden={!expandedSections.preview}>
              {/* Segment Response Preview */}
              <div className="preview-section">
                <label className="preview-label">Segment Response</label>
                <p className="preview-helper">
                  Generated for each segment in the video. The AI analyzes individual segments and returns this structure.
                </p>
                <pre className="response-preview-json">
{segmentPreviewFields.length > 0 ? JSON.stringify(generateExampleObject(segmentPreviewFields), null, 2) : '{\n  // Add fields to see preview\n}'}
                </pre>
              </div>

              {/* Video-Level Response Preview */}
              {(videoLevelEnabled || (!isEditing && prompt?.video_level)) && (
                <div className="preview-section">
                  <label className="preview-label">Video-Level Response</label>
                  <p className="preview-helper">
                    Generated once per video after all segments are processed. Uses the selected segment fields to provide a consolidated analysis.
                  </p>
                <pre className="response-preview-json">
{videoLevelPreviewFields.length > 0
  ? JSON.stringify(
      generateExampleObject(videoLevelPreviewFields),
      null,
      2
    )
  : '{\n  // Add fields to see preview\n}'}
                  </pre>
                </div>
              )}
                </div>
              </div>

              </div>
              </div>

              {hasProcessingStage && <div className="engine-editor-stage" ref={(node) => { stageRefs.current.processing = node; }}>
                {renderStageHeader('processing')}
              <div className="engine-editor-stage-body" id="engine-editor-panel-processing" role="region" aria-labelledby="engine-editor-stage-processing" hidden={!expandedStages.processing}>
                {isCreating && (
                  <div className="prompt-assistant-section">
                    <div className="prompt-assistant-header">
                      <div>
                        <div className="prompt-assistant-label">Assistant</div>
                        <div className="prompt-assistant-subtitle">
                          Describe what to extract or find. The assistant replaces this draft and adds optional filters when your goal calls for a shortlist.
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`prompt-assistant-toggle ${assistantPanelOpen ? 'active' : ''}`}
                        onClick={() => {
                          setAssistantPanelOpen((previous) => !previous);
                          setAssistantError(null);
                        }}
                        disabled={assistantIsLoading}
                        title="Open extraction engine assistant"
                      >
                        <Wand2 size={14} />
                        <span>Assistant</span>
                      </button>
                    </div>
                    {assistantPanelOpen && (
                      <div className="prompt-assistant-panel">
                        <textarea
                          className="prompt-assistant-textarea"
                          value={assistantPrompt}
                          onChange={(event) => {
                            setAssistantPrompt(event.target.value);
                            setAssistantError(null);
                          }}
                          placeholder="Describe the fields to extract, or the segments you want to find or shortlist."
                          maxLength={12000}
                          disabled={assistantIsLoading}
                        />
                        <div className="prompt-assistant-actions">
                          <button
                            type="button"
                            className="prompt-assistant-generate-btn"
                            onClick={() => {
                              void generatePromptAssistantDraft();
                            }}
                            disabled={assistantIsLoading || !assistantPrompt.trim()}
                          >
                            {assistantIsLoading ? 'Generating...' : 'Generate Draft'}
                          </button>
                        </div>
                        {assistantError && (
                          <div className="message error prompt-assistant-error">
                            <AlertCircle size={16} />
                            {assistantError}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!isRealtime && <PromptExecutionConfigEditor
                  value={executionConfig}
                  onChange={setExecutionConfig}
                  editable={isEditing && !assistantIsLoading}
                />}
              </div>
              </div>}

              <div className="engine-editor-stage" ref={(node) => { stageRefs.current.filters = node; }}>
                {renderStageHeader('filters')}
              <div className="engine-editor-stage-body" id="engine-editor-panel-filters" role="region" aria-labelledby="engine-editor-stage-filters" hidden={!expandedStages.filters}>
                {isRealtime && <section className="realtime-rubric" aria-label="Alert rubric">
                  <h3>Alert rubric</h3><p>Describe what each color means for this feed. These instructions are saved with the engine.</p>
                  <RealtimeAlertRubric value={realtimeConfig} onChange={isEditing ? setRealtimeConfig : undefined} />
                </section>}
                <WorkflowFilterEditor
                  value={selectionFilter}
                  segmentFields={segmentSelectionFields}
                  videoFields={videoSelectionFields}
                  onChange={setSelectionFilter}
                  editable={isEditing}
                />
              </div>
              </div>

            </div>

          </div>
        </div>
              {/* Action Buttons */}
              {isEditing && (
                <div className="sidebar-actions engine-editor-footer">
                  <button
                    className="cancel-button"
                    onClick={() => {
                      if (isCreating) {
                        suppressCreateDraftPublishRef.current = true;
                        onDiscardCreateDraft?.();
                        onClose();
                      } else {
                        restorePromptView();
                      }
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="save-button"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>Saving...</>
                    ) : (
                      <>
                        <Save size={16} />
                        {saveButtonLabel}
                      </>
                    )}
                  </button>
                </div>
              )}
      </div>
    </>
  );
};

export default PromptDetailSidebar;
